import {
    AccountingEntityType,
    OtrSolutionsBrokerCheck,
    OtrSolutionsSyncResult,
    TafsDebtorSearchResult,
    WexDebtor,
    WexFundingMethod,
    WexPreValidationResult,
} from '@numeo/types-main-web-app'
import { getApiBaseUrlWithoutV1 } from '@/setup/SetupAxios'
import {
    AccountingSettings,
    BaseEmailInvoice,
    BrokerInvoiceSource,
    BrokerInvoiceAccessorial,
    BrokerInvoiceStatus,
    BrokerInvoiceType,
    ClientApplication,
    ClientInvoice,
    ClientOngoingLoad,
    DbBrokerInvoice,
    GenerateBrokerInvoiceRequest,
    InvoiceDateBasis,
    InvoiceEmailActivityResponse,
    InvoiceStatusSummary,
    InvoiceMigrationResult as InvoiceMigrationCounts,
    InvoiceNote,
    InvoiceOtherDeduction,
    InvoiceRowsQuery,
    InvoiceRowsResponse,
    InvoiceStatsResponse,
    InvoiceStatus,
    InvoiceTripItem,
    OverviewDashboardData,
    PaymentTerms,
    RTSBankAccountOption,
    RTSBankAccountSplit,
    RTSSyncResult,
} from '@numeo/types-core'
import axios from 'axios'

export interface GenerateInvoicePayload {
    driverId: string
    startDate: string
    endDate: string
    trips: InvoiceTripItem[]
    otherDeductions?: InvoiceOtherDeduction[]
    /**
     * The statement being re-materialized. Required in the batch era, where a driver can hold
     * several statements for one period and the server cannot otherwise tell which one is meant.
     */
    statementId?: string
    /** Custom email subject; only used by send-email calls, falls back to the default statement subject when omitted. */
    emailSubject?: string
    /** Custom email body; only used by send-email calls, falls back to the default statement body when omitted. */
    emailBody?: string
}

interface GenerateInvoiceResponse {
    success: boolean
    data?: ClientInvoice
    message?: string
}

interface ListInvoicesParams {
    driverId?: string
    status?: InvoiceStatus
    page?: number
    limit?: number
}

interface ListInvoicesResponse {
    success: boolean
    data?: {
        items: ClientInvoice[]
        total: number
    }
    message?: string
}

interface GetInvoiceResponse {
    success: boolean
    data?: ClientInvoice
    message?: string
}

/**
 * Generate driver invoice/settlement statement
 */
export const generateDriverInvoice = async (payload: GenerateInvoicePayload): Promise<GenerateInvoiceResponse> => {
    try {
        const response = await axios.post<GenerateInvoiceResponse>('/accountant/driver-invoice/generate', payload)
        return response.data
    } catch (error) {
        console.error('Error generating driver invoice:', error)
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to generate invoice' }
        }
        return { success: false, message: 'Failed to generate invoice' }
    }
}

/**
 * List all driver invoices
 */
export const listDriverInvoices = async (params?: ListInvoicesParams): Promise<ListInvoicesResponse> => {
    try {
        const queryParams = new URLSearchParams()
        if (params?.driverId) queryParams.append('driverId', params.driverId)
        if (params?.status) queryParams.append('status', params.status)
        if (params?.page) queryParams.append('page', params.page.toString())
        if (params?.limit) queryParams.append('limit', params.limit.toString())

        const url = `/accountant/driver-invoice${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
        const response = await axios.get<ListInvoicesResponse>(url)
        return response.data
    } catch (error) {
        console.error('Error listing driver invoices:', error)
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to list invoices' }
        }
        return { success: false, message: 'Failed to list invoices' }
    }
}

/**
 * Get a single driver invoice by ID
 */
const getDriverInvoice = async (invoiceId: string): Promise<GetInvoiceResponse> => {
    try {
        const response = await axios.get<GetInvoiceResponse>(`/accountant/driver-invoice/${invoiceId}`)
        return response.data
    } catch (error) {
        console.error('Error getting driver invoice:', error)
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to get invoice' }
        }
        return { success: false, message: 'Failed to get invoice' }
    }
}

/**
 * Delete a driver invoice
 */
const deleteDriverInvoice = async (invoiceId: string): Promise<{ success: boolean; message?: string }> => {
    try {
        const response = await axios.delete(`/accountant/driver-invoice/${invoiceId}`)
        return response.data
    } catch (error) {
        console.error('Error deleting driver invoice:', error)
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to delete invoice' }
        }
        return { success: false, message: 'Failed to delete invoice' }
    }
}

// ==================== BROKER INVOICE API ====================

interface GenerateBrokerInvoicePayload extends GenerateBrokerInvoiceRequest {
    loadIds: string[]
    customInvoiceNumber?: string
    paymentTerms?: PaymentTerms
    notes?: string
    status?: BrokerInvoiceStatus
    batchName?: string
    type?: BrokerInvoiceType
    debtorOverrides?: Record<string, TriumphDebtorResult>
    mergeInvoices?: boolean
    /** Artispro-only: the dispatcher-selected payment type label (e.g. "Wire"). */
    artisproPaymentType?: string
    /** RTS-only: manual 2-account payout split. Only honored when the project's RTS bank-account-selection setting is enabled. */
    rtsBankAccountSplit?: RTSBankAccountSplit
}

interface GenerateBrokerInvoiceResponse {
    success: boolean
    data?: DbBrokerInvoice
    message?: string
    errorCode?: string
    rts?: { purchaseId: string; rtsInvoiceId: string; hasDuplicates: boolean } | null
    factoringError?: string
    pdfError?: string
}

interface ListBrokerInvoicesParams {
    status?: BrokerInvoiceStatus
    brokerMc?: string
    page?: number
    limit?: number
    type?: BrokerInvoiceType
}

interface ListBrokerInvoicesResponse {
    success: boolean
    data?: {
        invoices: DbBrokerInvoice[]
        total: number
    }
    message?: string
}

interface GetBrokerInvoiceResponse {
    success: boolean
    data?: DbBrokerInvoice
    message?: string
}

/**
 * Generate broker invoice from one or more loads
 */
const generateBrokerInvoice = async (payload: GenerateBrokerInvoicePayload): Promise<GenerateBrokerInvoiceResponse> => {
    try {
        const response = await axios.post<GenerateBrokerInvoiceResponse>('/accountant/broker-invoice', payload)
        return response.data
    } catch (error) {
        console.error('Error generating broker invoice:', error)
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to generate broker invoice', errorCode: error.response?.data?.errorCode }
        }
        return { success: false, message: 'Failed to generate broker invoice' }
    }
}

/**
 * List all broker invoices
 */
const listBrokerInvoices = async (params?: ListBrokerInvoicesParams): Promise<ListBrokerInvoicesResponse> => {
    try {
        const queryParams = new URLSearchParams()
        if (params?.status) queryParams.append('status', params.status)
        if (params?.brokerMc) queryParams.append('brokerMc', params.brokerMc)
        if (params?.type) queryParams.append('type', params.type)
        if (params?.page) queryParams.append('page', params.page.toString())
        if (params?.limit) queryParams.append('limit', params.limit.toString())

        const url = `/accountant/broker-invoice${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
        const response = await axios.get<ListBrokerInvoicesResponse>(url)
        return response.data
    } catch (error) {
        console.error('Error listing broker invoices:', error)
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to list broker invoices' }
        }
        return { success: false, message: 'Failed to list broker invoices' }
    }
}

/**
 * List server-computed invoice display rows (BATCH grouped + STANDARD + invoiced-load pseudo-rows),
 * sorted/paginated/filtered in Mongo. Replaces the client-side fetch-all + grouping.
 */
export const listInvoiceRows = async (
    query: InvoiceRowsQuery,
    signal?: AbortSignal
): Promise<{ success: boolean; data?: InvoiceRowsResponse; message?: string }> => {
    try {
        const qp = new URLSearchParams()
        qp.append('tab', query.tab)
        qp.append('page', String(query.page))
        qp.append('limit', String(query.limit))
        if (query.search) qp.append('search', query.search)
        if (query.shortPaid) qp.append('shortPaid', 'true')
        if (query.batchOnly) qp.append('batchOnly', 'true')
        if (query.appendableOnly) qp.append('appendableOnly', 'true')
        if (query.includeTruckUnitNumber) qp.append('includeTruckUnitNumber', 'true')
        if (query.includeDriverName) qp.append('includeDriverName', 'true')
        if (query.includeLoadDetails) qp.append('includeLoadDetails', 'true')
        if (query.keepBatchesWhole) qp.append('keepBatchesWhole', 'true')
        if (query.dateFrom) qp.append('dateFrom', query.dateFrom)
        if (query.dateTo) qp.append('dateTo', query.dateTo)
        if (query.sortField) qp.append('sortField', query.sortField)
        if (query.sortDirection) qp.append('sortDirection', query.sortDirection)
        if (query.timezone) qp.append('timezone', query.timezone)
        query.status?.forEach((s) => qp.append('status', s))
        const response = await axios.get(`/accountant/broker-invoice/rows?${qp.toString()}`, { signal })
        return response.data
    } catch (error) {
        if (axios.isAxiosError(error)) return { success: false, message: error.response?.data?.message || 'Failed to list invoice rows' }
        return { success: false, message: 'Failed to list invoice rows' }
    }
}

/**
 * Get filter-aware invoice stats (respects the active search + status filter).
 */
export const getInvoiceStats = async (
    filters: Pick<InvoiceRowsQuery, 'search' | 'status' | 'dateFrom' | 'dateTo'> = {}
): Promise<{ success: boolean; data?: InvoiceStatsResponse; message?: string }> => {
    try {
        const qp = new URLSearchParams()
        if (filters.search) qp.append('search', filters.search)
        if (filters.dateFrom) qp.append('dateFrom', filters.dateFrom)
        if (filters.dateTo) qp.append('dateTo', filters.dateTo)
        filters.status?.forEach((s) => qp.append('status', s))
        const qs = qp.toString()
        const response = await axios.get(`/accountant/broker-invoice/stats${qs ? `?${qs}` : ''}`)
        return response.data
    } catch (error) {
        if (axios.isAxiosError(error)) return { success: false, message: error.response?.data?.message || 'Failed to get invoice stats' }
        return { success: false, message: 'Failed to get invoice stats' }
    }
}

/**
 * List internal notes for an invoice (newest-first). Internal-only — never shared with brokers.
 */
export const listInvoiceNotes = async (invoiceId: string): Promise<{ success: boolean; notes?: InvoiceNote[]; message?: string }> => {
    try {
        const response = await axios.get(`/accountant/broker-invoice/${invoiceId}/notes`)
        return { success: true, notes: response.data?.data?.notes ?? [] }
    } catch (error) {
        if (axios.isAxiosError(error)) return { success: false, message: error.response?.data?.message || 'Failed to load notes' }
        return { success: false, message: 'Failed to load notes' }
    }
}

/**
 * Add an internal note to an invoice. Returns the new note + the full updated list (newest-first).
 */
export const addInvoiceNote = async (
    invoiceId: string,
    text: string
): Promise<{ success: boolean; note?: InvoiceNote; notes?: InvoiceNote[]; message?: string }> => {
    try {
        const response = await axios.post(`/accountant/broker-invoice/${invoiceId}/notes`, { text })
        return { success: true, note: response.data?.data?.note, notes: response.data?.data?.notes ?? [] }
    } catch (error) {
        if (axios.isAxiosError(error)) return { success: false, message: error.response?.data?.message || 'Failed to add note' }
        return { success: false, message: 'Failed to add note' }
    }
}

/**
 * Get a single broker invoice by ID
 */
export const getBrokerInvoice = async (invoiceId: string): Promise<GetBrokerInvoiceResponse> => {
    try {
        const response = await axios.get<GetBrokerInvoiceResponse>(`/accountant/broker-invoice/${invoiceId}`)
        return response.data
    } catch (error) {
        console.error('Error getting broker invoice:', error)
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to get broker invoice' }
        }
        return { success: false, message: 'Failed to get broker invoice' }
    }
}

/**
 * Update broker invoice status
 */
const updateBrokerInvoiceStatus = async (
    invoiceId: string,
    status: BrokerInvoiceStatus,
    paidAmount?: number,
    manualOverride?: boolean,
    revertSettlement?: boolean,
    /** Marking an invoice as sent for a send made outside Numeo: the date it carries is left as it was. */
    preserveSubmittedAt?: boolean
): Promise<{ success: boolean; data?: DbBrokerInvoice; message?: string }> => {
    try {
        const response = await axios.put(`/accountant/broker-invoice/${invoiceId}/status`, {
            status,
            paidAmount,
            manualOverride,
            revertSettlement,
            preserveSubmittedAt,
        })
        return response.data
    } catch (error) {
        console.error('Error updating broker invoice status:', error)
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to update invoice status' }
        }
        return { success: false, message: 'Failed to update invoice status' }
    }
}

const generateInvoicePdf = async (invoiceId: string): Promise<{ success: boolean; data?: { pdfUrl: string }; message?: string }> => {
    try {
        const response = await axios.post(`/accountant/broker-invoice/${invoiceId}/generate-pdf`)
        return response.data
    } catch (error) {
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to generate PDF' }
        }
        return { success: false, message: 'Failed to generate PDF' }
    }
}

type InvoicePdfResult = { ok: true; pdfUrl: string; invoiceNumber?: string } | { ok: false; message: string }

/**
 * The PDF of a broker invoice, however it is held: the stored file, one fetched from RTS / Triumph / WEX, or, for a standard invoice with
 * no file yet, one generated now and cached. Shared by the Invoices page and a load's Documents.
 */
const resolveInvoicePdf = async (invoiceId: string): Promise<InvoicePdfResult> => {
    const response = await getBrokerInvoice(invoiceId)
    if (!response.success || !response.data) return { ok: false, message: response.message || 'Failed to fetch invoice details' }
    const invoice = response.data
    const existingPdfUrl = invoice.pdfUrl || invoice.rateConUrl
    if (existingPdfUrl) return { ok: true, pdfUrl: existingPdfUrl, invoiceNumber: invoice.invoiceNumber }

    const fetched = async (
        request: Promise<{ success: boolean; data?: { pdfUrl: string }; message?: string }>,
        unavailable: string
    ): Promise<InvoicePdfResult> => {
        const result = await request
        return result.success && result.data?.pdfUrl
            ? { ok: true, pdfUrl: result.data.pdfUrl, invoiceNumber: invoice.invoiceNumber }
            : { ok: false, message: result.message || unavailable }
    }
    const NOT_READY = 'PDF is not available for this invoice yet.'
    if (invoice.source === BrokerInvoiceSource.RTS) return fetched(fetchRtsInvoicePdf(invoiceId), NOT_READY)
    if (invoice.source === BrokerInvoiceSource.TRIUMPH) return fetched(fetchTriumphInvoicePdf(invoiceId), NOT_READY)
    if (invoice.wexBillingId) return fetched(fetchWexInvoicePdf(invoiceId), NOT_READY)
    // Standard invoice with no PDF yet: generate on demand and cache pdfUrl
    const invId = invoice._id?.toString() || (invoice as DbBrokerInvoice & { id?: string }).id
    if (!invId) return { ok: false, message: 'PDF is not available for this invoice.' }
    return fetched(generateInvoicePdf(invId), 'PDF could not be generated for this invoice.')
}

interface BulkInvoiceStatusUpdate {
    invoiceId: string
    status: BrokerInvoiceStatus
    paidAmount?: number
    /** Only the amount is being corrected on an invoice that keeps its status: saved, but not logged again as a payment or a status change. */
    amountOnly?: boolean
}

interface BulkInvoiceStatusResult {
    updatedCount: number
    failedCount: number
    errors: { invoiceId: string; error: string }[]
}

const bulkUpdateBrokerInvoiceStatus = async (
    updates: BulkInvoiceStatusUpdate[]
): Promise<{ success: boolean; data?: BulkInvoiceStatusResult; message?: string }> => {
    try {
        const response = await axios.put('/accountant/broker-invoice/bulk/status', { updates })
        return response.data
    } catch (error) {
        console.error('Error bulk updating broker invoice status:', error)
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to update invoice statuses' }
        }
        return { success: false, message: 'Failed to update invoice statuses' }
    }
}

interface BulkCloseLoadsResult {
    invoiceIds: string[]
    failed: { loadId: string; error: string }[]
}

const bulkCloseLoads = async (
    loads: { loadId: string; paidAmount: number }[]
): Promise<{ success: boolean; data?: BulkCloseLoadsResult; message?: string }> => {
    try {
        const response = await axios.post('/accountant/broker-invoice/bulk/close', { loads })
        return response.data
    } catch (error) {
        console.error('Error bulk closing loads:', error)
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to bulk close loads' }
        }
        return { success: false, message: 'Failed to bulk close loads' }
    }
}

/**
 * Delete a broker invoice
 */
const deleteBrokerInvoice = async (invoiceId: string): Promise<{ success: boolean; message?: string }> => {
    try {
        const response = await axios.delete(`/accountant/broker-invoice/${invoiceId}`)
        return response.data
    } catch (error) {
        console.error('Error deleting broker invoice:', error)
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to delete broker invoice' }
        }
        return { success: false, message: 'Failed to delete broker invoice' }
    }
}

const removeInvoicesFromBatch = async (
    batchName: string,
    invoiceIds: string[]
): Promise<{
    success: boolean
    message?: string
    data?: { requested: number; deleted: number; ungrouped: number; loadsReset: number; loadsKeptInvoiced: number }
}> => {
    try {
        const response = await axios.post('/accountant/broker-invoice/batch/remove-invoices', { batchName, invoiceIds })
        return response.data
    } catch (error) {
        console.error('Error removing invoices from batch:', error)
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to remove invoices from batch' }
        }
        return { success: false, message: 'Failed to remove invoices from batch' }
    }
}

/**
 * Get the suggested next invoice number (does not consume the counter) — pre-fills the editable
 * Invoice Number field on the create modal.
 */
export const getNextInvoiceNumber = async (loadId?: string): Promise<string | null> => {
    try {
        const response = await axios.get('/accountant/broker-invoice/next-number', loadId ? { params: { loadId } } : undefined)
        return response.data?.data?.invoiceNumber ?? null
    } catch (error) {
        console.error('Error fetching next invoice number:', error)
        return null
    }
}

/**
 * Update invoice data for a load directly (without requiring an invoice).
 *
 * `accessorials[]` is the single source of truth: the server derives the legacy flat charges
 * (fuelSurcharge / detentionCharge / lumperFee) from it, so they are deliberately not sent.
 */
const updateLoadInvoiceData = async (
    loadId: string,
    updates: {
        accessorials?: BrokerInvoiceAccessorial[]
        totalAmount: number
        lineHaulRate?: number
    }
): Promise<{ success: boolean; data?: ClientOngoingLoad; message?: string }> => {
    try {
        const response = await axios.put(`/accountant/broker-invoice/loads/${loadId}/accessorials`, updates)
        return response.data
    } catch (error) {
        console.error('Error updating load invoice data:', error)
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to update invoice data' }
        }
        return { success: false, message: 'Failed to update invoice data' }
    }
}

/**
 * Push a corrected broker rate onto a load's accounting records (load_invoice_data and, when the load
 * has one, its broker invoice).
 *
 * The ONLY edit permitted on an already-invoiced load. Without `acknowledgeLockedInvoice` such a load
 * comes back `status: 'locked'` and nothing is written, so the caller can warn first and ask again.
 */
const updateLoadLineHaulRate = async (
    loadId: string,
    lineHaulRate: number,
    options: { acknowledgeLockedInvoice?: boolean } = {}
): Promise<{
    success: boolean
    message?: string
    data?: { applied: boolean; status: 'synced' | 'unchanged' | 'locked' | 'tonu'; previousRate?: number; invoiceNumber?: string }
}> => {
    try {
        const response = await axios.put(`/accountant/broker-invoice/loads/${loadId}/line-haul-rate`, {
            lineHaulRate,
            acknowledgeLockedInvoice: options.acknowledgeLockedInvoice === true,
        })
        return response.data
    } catch (error) {
        console.error('Error updating load line haul rate:', error)
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to update the invoiced rate' }
        }
        return { success: false, message: 'Failed to update the invoiced rate' }
    }
}

/**
 * Send driver invoice via email
 */
export const sendDriverInvoiceEmail = async (payload: GenerateInvoicePayload): Promise<{ success: boolean; message?: string }> => {
    try {
        const response = await axios.post<{ success: boolean; message?: string }>('/accountant/driver-invoice/send-email', payload)
        return response.data
    } catch (error) {
        console.error('Error sending driver invoice email:', error)
        if (axios.isAxiosError(error)) {
            // `message` is the NumeoRestError shape; `error` covers responses from older deploys.
            return { success: false, message: error.response?.data?.message || error.response?.data?.error || 'Failed to send email' }
        }
        return { success: false, message: 'Failed to send email' }
    }
}

/**
 * Bulk send driver invoice emails
 */
export const bulkSendDriverInvoiceEmails = async (
    payloads: GenerateInvoicePayload[]
): Promise<{
    success: boolean
    message?: string
    successCount?: number
    failCount?: number
    results?: { driverId: string; success: boolean; error?: string }[]
}> => {
    try {
        const response = await axios.post<{
            success: boolean
            message?: string
            successCount?: number
            failCount?: number
            results?: { driverId: string; success: boolean; error?: string }[]
        }>('/accountant/driver-invoice/bulk-send-email', { requests: payloads })
        return response.data
    } catch (error) {
        if (axios.isAxiosError(error)) {
            // `message` is the NumeoRestError shape; `error` covers responses from older deploys.
            return { success: false, message: error.response?.data?.message || error.response?.data?.error || 'Failed to send emails' }
        }
        return { success: false, message: 'Failed to send emails' }
    }
}

/**
 * Send a broker invoice via email
 */
const sendEmailInvoice = async (invoiceId: string, data: BaseEmailInvoice): Promise<{ success: boolean; message?: string }> => {
    try {
        const response = await axios.post(`/accountant/broker-invoice/${invoiceId}/send-email`, data)
        return response.data
    } catch (error) {
        console.error('Error sending invoice email:', error)
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to send email' }
        }
        return { success: false, message: 'Failed to send email' }
    }
}

/**
 * Get merged PDF for a batch of invoices
 */
const getBatchMergedPdf = async (batchName: string): Promise<{ success: boolean; data?: { url: string; invoiceCount: number }; message?: string }> => {
    try {
        const response = await axios.get(`/accountant/broker-invoice/batch/${encodeURIComponent(batchName)}/pdf`)
        return response.data
    } catch (error) {
        console.error('Error getting batch merged PDF:', error)
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to get batch PDF' }
        }
        return { success: false, message: 'Failed to get batch PDF' }
    }
}

/**
 * Create a batch of per-load invoices, optionally submitting to factoring
 */
const createBatch = async (payload: {
    loadIds: string[]
    batchName?: string
    appendToExistingBatch?: boolean
    submitToFactoring?: boolean
    paymentTerms?: PaymentTerms
    mergeInvoices?: boolean
    debtorOverrides?: Record<string, TriumphDebtorResult>
    /** Dispatcher-confirmed bill-to corrections, keyed by load id — renames the invoice + PDF bill-to. */
    billToOverrides?: Record<string, { companyName: string; mcNumber?: string }>
    /** WEX-only: 'Wire' or 'ACH' (translated from Standard/Quick on the hook side). */
    wexFundingMethod?: string
    /** Artispro-only: the dispatcher-selected payment type label (e.g. "Wire"). */
    artisproPaymentType?: string
    /** RTS-only: manual 2-account payout split. Only honored when the project's RTS bank-account-selection setting is enabled. */
    rtsBankAccountSplit?: RTSBankAccountSplit
}): Promise<{
    success: boolean
    data?: { invoices: DbBrokerInvoice[]; batchName: string }
    rts?: unknown
    factoringError?: string
    pdfError?: string
    message?: string
    errorCode?: string
}> => {
    try {
        const response = await axios.post('/accountant/broker-invoice/batch', payload)
        return response.data
    } catch (error) {
        console.error('Error creating batch:', error)
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to create batch', errorCode: error.response?.data?.errorCode }
        }
        return { success: false, message: 'Failed to create batch' }
    }
}

/**
 * Collect invoices that already exist into one named batch.
 *
 * The Ready-to-Invoice flow batches loads before they are invoiced (`createBatch`); this is the
 * after-the-fact equivalent for invoices already sitting on the Invoiced tab. It only regroups — no
 * invoice is created and nothing is submitted to a factor.
 */
const groupInvoicesIntoBatch = async (payload: {
    invoiceIds: string[]
    batchName?: string
    appendToExistingBatch?: boolean
}): Promise<{
    success: boolean
    data?: { batchName: string; invoiceCount: number }
    message?: string
    /** Ids the server could not resolve — the caller maps them back to rows to name them. */
    missingInvoiceIds?: string[]
}> => {
    try {
        const response = await axios.post('/accountant/broker-invoice/batch/group', payload)
        return response.data
    } catch (error) {
        console.error('Error grouping invoices into a batch:', error)
        if (axios.isAxiosError(error)) {
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to create batch',
                missingInvoiceIds: error.response?.data?.metadata?.missingInvoiceIds,
            }
        }
        return { success: false, message: 'Failed to create batch' }
    }
}

/**
 * Fetch the Artispro payment types the dispatcher can choose from at submit time.
 * Returns the option labels (Wire, ACH, RTP, …); the chosen label is sent back as `artisproPaymentType`.
 */
export const fetchArtisproPaymentTypes = async (): Promise<string[]> => {
    // Throws on failure so the selector can distinguish a fetch error (show a reconnect hint)
    // from a genuinely empty list.
    const response = await axios.get<{ success: boolean; data?: { label: string }[] }>('/factoring/artispro/payment-types')
    return (response.data?.data || []).map((o) => o.label)
}

// Same reasoning as RTSBankAccountSplit: one wire shape, defined once in @numeo/types.
export type { RTSBankAccountOption }

// Re-exported so existing `from 'services/invoiceApi'` imports keep working while the single
// source of truth stays in @numeo/types (the backend persists this shape on the invoice).
export type { RTSBankAccountSplit }

/**
 * Fetch the project's linked RTS bank accounts for the manual split picker.
 * `paymentTerms` mirrors the backend's own auto-pick logic so `isRecommended`
 * reflects whichever account today's default behavior would have chosen.
 */
export const fetchRtsBankAccounts = async (paymentTerms?: PaymentTerms): Promise<{ success: boolean; data?: RTSBankAccountOption[]; message?: string }> => {
    try {
        const response = await axios.get<{ success: boolean; data?: RTSBankAccountOption[]; message?: string }>(
            '/accountant/broker-invoice/rts/bank-accounts',
            { params: paymentTerms ? { paymentTerms } : undefined }
        )
        return response.data
    } catch (error) {
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to fetch RTS bank accounts' }
        }
        return { success: false, message: 'Failed to fetch RTS bank accounts' }
    }
}

/**
 * Sync invoices from RTS to local DB
 */
export const syncRTSInvoices = async (dateFrom?: string, dateTo?: string): Promise<{ success: boolean; data?: RTSSyncResult; message?: string }> => {
    try {
        const response = await axios.post<{ success: boolean; data?: RTSSyncResult; message?: string }>('/rts/sync', { dateFrom, dateTo })
        return response.data
    } catch (error) {
        console.error('Error syncing RTS invoices:', error)
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to sync RTS invoices' }
        }
        return { success: false, message: 'Failed to sync RTS invoices' }
    }
}

/**
 * Sync invoices from WinFactor (PRO Funding) to local DB
 */
const syncWinfactorInvoices = async (): Promise<{ success: boolean; data?: { jobId: string }; message?: string }> => {
    try {
        const response = await axios.post<{ success: boolean; data?: { jobId: string }; message?: string }>('/factoring/winfactor/sync')
        return response.data
    } catch (error) {
        console.error('Error syncing WinFactor invoices:', error)
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to sync WinFactor invoices' }
        }
        return { success: false, message: 'Failed to sync WinFactor invoices' }
    }
}

/**
 * Sync invoices from Stratopay (Addio) factoring
 */
const syncStratopayInvoices = async (): Promise<{ success: boolean; data?: { synced: number; total: number }; message?: string }> => {
    try {
        const response = await axios.post<{ success: boolean; data?: { synced: number; total: number }; message?: string }>('/factoring/stratopay/sync')
        return response.data
    } catch (error) {
        console.error('Error syncing Stratopay invoices:', error)
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to sync Stratopay invoices' }
        }
        return { success: false, message: 'Failed to sync Stratopay invoices' }
    }
}

/**
 * Sync invoices from Artispro factoring
 */
/**
 * Reconcile the status of invoices submitted to OTR Solutions. OTR exposes no "list my invoices"
 * endpoint, so this only touches invoices Numeo submitted — it never imports history.
 */
const syncOtrSolutionsInvoices = async (): Promise<{ success: boolean; data?: OtrSolutionsSyncResult; message?: string }> => {
    try {
        const response = await axios.post<{ success: boolean; data?: OtrSolutionsSyncResult; message?: string }>('/factoring/otr-solutions/sync')
        return response.data
    } catch (error) {
        console.error('Error syncing OTR Solutions invoices:', error)
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to sync OTR Solutions invoices' }
        }
        return { success: false, message: 'Failed to sync OTR Solutions invoices' }
    }
}

const syncArtisproInvoices = async (): Promise<{ success: boolean; data?: { updated: number; skipped: number }; message?: string }> => {
    try {
        const response = await axios.post<{ success: boolean; data?: { updated: number; skipped: number }; message?: string }>('/factoring/artispro/sync')
        return response.data
    } catch (error) {
        console.error('Error syncing Artispro invoices:', error)
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to sync Artispro invoices' }
        }
        return { success: false, message: 'Failed to sync Artispro invoices' }
    }
}

/**
 * Sync invoices from Triumph factoring
 */
const syncTriumphInvoices = async (dateFrom?: string, dateTo?: string): Promise<{ success: boolean; data?: { jobId: string }; message?: string }> => {
    try {
        const response = await axios.post<{ success: boolean; data?: { jobId: string }; message?: string }>('/triumph/sync', { dateFrom, dateTo })
        return response.data
    } catch (error) {
        console.error('Error syncing Triumph invoices:', error)
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to sync Triumph invoices' }
        }
        return { success: false, message: 'Failed to sync Triumph invoices' }
    }
}

/**
 * Fetch PDF for a broker invoice from RTS on demand.
 * Returns pdfUrl if found, or null.
 */
const fetchRtsInvoicePdf = async (invoiceId: string): Promise<{ success: boolean; data?: { pdfUrl: string }; message?: string }> => {
    try {
        const response = await axios.post<{ success: boolean; data?: { pdfUrl: string }; message?: string }>(
            `/accountant/broker-invoice/${invoiceId}/fetch-rts-pdf`
        )
        return response.data
    } catch (error) {
        console.error('Error fetching RTS invoice PDF:', error)
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to fetch PDF from RTS' }
        }
        return { success: false, message: 'Failed to fetch PDF from RTS' }
    }
}

/**
 * Fetch PDF for a broker invoice from Triumph on demand.
 */
const fetchTriumphInvoicePdf = async (invoiceId: string): Promise<{ success: boolean; data?: { pdfUrl: string }; message?: string }> => {
    try {
        const response = await axios.post<{ success: boolean; data?: { pdfUrl: string }; message?: string }>(`/triumph/invoice/${invoiceId}/fetch-pdf`)
        return response.data
    } catch (error) {
        console.error('Error fetching Triumph invoice PDF:', error)
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to fetch PDF from Triumph' }
        }
        return { success: false, message: 'Failed to fetch PDF from Triumph' }
    }
}

const fetchWexInvoicePdf = async (invoiceId: string): Promise<{ success: boolean; data?: { pdfUrl: string }; message?: string }> => {
    try {
        const response = await axios.get<Blob>(`/wex/invoice/${invoiceId}/pdf`, { responseType: 'blob' })
        const pdfUrl = URL.createObjectURL(response.data)
        return { success: true, data: { pdfUrl } }
    } catch (error) {
        if (axios.isAxiosError(error)) {
            const blob = error.response?.data
            if (blob instanceof Blob) {
                try {
                    const json = JSON.parse(await blob.text())
                    console.error('[WEX] fetchWexInvoicePdf failed:', error.response?.status, json)
                    return { success: false, message: json?.message || 'Failed to fetch PDF from WEX' }
                } catch {
                    console.error('[WEX] fetchWexInvoicePdf failed:', error.response?.status, error.response?.data)
                    return { success: false, message: 'Failed to fetch PDF from WEX' }
                }
            }
            console.error('[WEX] fetchWexInvoicePdf failed:', error.response?.status, error.response?.data)
            return { success: false, message: 'Failed to fetch PDF from WEX' }
        }
        console.error('[WEX] fetchWexInvoicePdf unexpected error:', error)
        return { success: false, message: 'Failed to fetch PDF from WEX' }
    }
}

/**
 * Get RTS job status by jobId (for polling)
 */
export const getRTSJobStatus = async (
    jobId: string
): Promise<{
    success: boolean
    data?: { jobId: string; status: string; progress: { current: number; total: number; message: string }; result?: unknown; error?: string }
    message?: string
}> => {
    try {
        const response = await axios.get(`/rts/job/${jobId}`)
        return response.data
    } catch (error) {
        console.error('Error getting RTS job status:', error)
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to get job status' }
        }
        return { success: false, message: 'Failed to get job status' }
    }
}

const retryBatchFactoring = async (
    batchName: string,
    artisproPaymentType?: string
): Promise<{ success: boolean; data?: { jobId: string }; message?: string; factoringWarning?: string }> => {
    try {
        const response = await axios.post('/accountant/broker-invoice/batch/retry-rts', { batchName, ...(artisproPaymentType && { artisproPaymentType }) })
        return response.data
    } catch (error) {
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to retry factoring submission' }
        }
        return { success: false, message: 'Failed to retry factoring submission' }
    }
}

/** @deprecated Use retryBatchFactoring instead */
const retryBatchRTS = retryBatchFactoring

/**
 * Retry factoring submission for a single invoice
 */
export const retryFactoring = async (
    invoiceId: string,
    debtorName?: string,
    artisproPaymentType?: string
): Promise<{ success: boolean; data?: DbBrokerInvoice; message?: string }> => {
    try {
        const response = await axios.post(`/accountant/broker-invoice/${invoiceId}/retry-factoring`, {
            ...(debtorName && { debtorName }),
            ...(artisproPaymentType && { artisproPaymentType }),
        })
        return response.data
    } catch (error) {
        console.error('Error retrying factoring:', error)
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to retry factoring' }
        }
        return { success: false, message: 'Failed to retry factoring' }
    }
}

const cancelBatchRTS = async (
    batchName: string,
    jobId?: string
): Promise<{ success: boolean; data?: { deleted: number; loadsUnmarked: number }; message?: string }> => {
    try {
        const response = await axios.post('/accountant/broker-invoice/batch/cancel', { batchName, jobId })
        return response.data
    } catch (error) {
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to cancel batch' }
        }
        return { success: false, message: 'Failed to cancel batch' }
    }
}

/**
 * Get email activity for invoice(s)
 */
export const getInvoiceEmailActivity = async (invoiceIds: string[]): Promise<{ success: boolean; data?: InvoiceEmailActivityResponse; message?: string }> => {
    try {
        const response = await axios.get(`/accountant/broker-invoice/email-activity?invoiceIds=${invoiceIds.join(',')}`)
        return response.data
    } catch (error) {
        console.error('Error fetching invoice email activity:', error)
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to fetch email activity' }
        }
        return { success: false, message: 'Failed to fetch email activity' }
    }
}

/** Where each of these invoices stands (its status, and whether a factor has it): what Ongoing Loads shows in its Finance column. */
export const getInvoiceStatusSummaries = async (
    invoiceIds: string[]
): Promise<{ success: boolean; data?: { items: InvoiceStatusSummary[] }; message?: string }> => {
    try {
        const response = await axios.get('/accounting/invoices/status-summaries/v1', {
            params: { ids: invoiceIds.join(',') },
            baseURL: getApiBaseUrlWithoutV1(),
        })
        return response.data
    } catch (error) {
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to fetch invoice statuses' }
        }
        return { success: false, message: 'Failed to fetch invoice statuses' }
    }
}

export const getAccountingReadStatus = async (
    entityIds: string[],
    entityType: AccountingEntityType
): Promise<{ success: boolean; data?: { readEntityIds: string[] }; message?: string }> => {
    try {
        const response = await axios.post<{ success: boolean; data: { readEntityIds: string[] } }>('/accountant/read-status/batch-get', {
            entityIds,
            entityType,
        })
        return response.data
    } catch (error) {
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to fetch read status' }
        }
        return { success: false, message: 'Failed to fetch read status' }
    }
}

export const toggleAccountingReadStatus = async (
    entityId: string,
    entityType: AccountingEntityType,
    isRead: boolean
): Promise<{ success: boolean; data?: { entityId: string; isRead: boolean }; message?: string }> => {
    try {
        const response = await axios.post<{ success: boolean; data: { entityId: string; isRead: boolean } }>('/accountant/read-status/toggle', {
            entityId,
            entityType,
            isRead,
        })
        return response.data
    } catch (error) {
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to toggle read status' }
        }
        return { success: false, message: 'Failed to toggle read status' }
    }
}

export const batchToggleAccountingReadStatus = async (
    entityIds: string[],
    entityType: AccountingEntityType,
    isRead: boolean
): Promise<{ success: boolean; data?: { affected: number; isRead: boolean }; message?: string }> => {
    try {
        const response = await axios.post<{ success: boolean; data: { affected: number; isRead: boolean } }>('/accountant/read-status/batch-toggle', {
            entityIds,
            entityType,
            isRead,
        })
        return response.data
    } catch (error) {
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to batch toggle read status' }
        }
        return { success: false, message: 'Failed to batch toggle read status' }
    }
}

/**
 * Get pre-aggregated overview stats for the collection dashboard
 */
export const getOverviewStats = async (
    startDate: string,
    endDate: string,
    dateBasis: InvoiceDateBasis = 'invoice'
): Promise<{ success: boolean; data?: OverviewDashboardData; message?: string }> => {
    try {
        const params = new URLSearchParams({ startDate, endDate, dateBasis })
        const response = await axios.get(`/accountant/broker-invoice/overview-stats?${params.toString()}`)
        return response.data
    } catch (error) {
        console.error('Error fetching overview stats:', error)
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to fetch overview stats' }
        }
        return { success: false, message: 'Failed to fetch overview stats' }
    }
}

// ==================== TRIUMPH DEBTOR API ====================

export interface TriumphDebtorResult {
    id: number | string
    name?: string
    mcNumber?: string
    dbaName?: string
    otherName?: string
    otherNames?: string[]
    address?: Record<string, string>
    isClientDebtorRelated?: boolean
    debtorNumbers?: Array<{ docket?: string; type?: number; typeDescription?: string; value?: string }>
    email?: string
    phone?: string
    isCompromised?: boolean
    isFirstUse?: boolean
    isNoBuy?: boolean
    isOBCNoBuy?: boolean
    /**
     * The override assigns a factoring debtor only — its name is the factor's stored debtor
     * name, NOT a dispatcher correction of the invoice bill-to. Consumers must not derive a
     * billToOverride from it. Set by name-matching providers (Stratopay).
     */
    factoringOnly?: boolean
}

export const searchTafsDebtors = async (
    searchTerm: string
): Promise<{ success: boolean; data?: TafsDebtorSearchResult[]; message?: string; needsReauth?: boolean }> => {
    try {
        const response = await axios.post<{ success: boolean; data?: TafsDebtorSearchResult[]; message?: string }>('/factoring/tafs/debtors/search', {
            searchTerm,
        })
        return response.data
    } catch (error) {
        if (axios.isAxiosError(error)) {
            const status = error.response?.status
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to search TAFS debtors',
                needsReauth: status === 401 || status === 403,
            }
        }
        return { success: false, message: 'Failed to search TAFS debtors' }
    }
}

export interface PreValidationResult {
    invoiceId: string
    invoiceNumber?: string
    brokerName: string
    mcNumber: string
    matchStatus: 'exact_mc' | 'no_match' | 'error'
    matchedDebtor?: TriumphDebtorResult
    searchResults: TriumphDebtorResult[]
    error?: string
}

interface PreValidationResponse {
    requireConfirmation: boolean
    results: PreValidationResult[]
}

export const searchTriumphDebtors = async (
    searchTerm: string
): Promise<{ success: boolean; data?: TriumphDebtorResult[]; message?: string; needsReauth?: boolean }> => {
    try {
        const response = await axios.post<{ success: boolean; data?: TriumphDebtorResult[]; message?: string }>('/triumph/debtors/search', { searchTerm })
        return response.data
    } catch (error) {
        if (axios.isAxiosError(error)) {
            const status = error.response?.status
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to search debtors',
                needsReauth: status === 401 || status === 403,
            }
        }
        return { success: false, message: 'Failed to search debtors' }
    }
}

export const preValidateTriumphDebtors = async (invoiceIds: string[]): Promise<{ success: boolean; data?: PreValidationResponse; message?: string }> => {
    try {
        const response = await axios.post<{ success: boolean; data?: PreValidationResponse; message?: string }>('/triumph/debtors/pre-validate', { invoiceIds })
        return response.data
    } catch (error) {
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to pre-validate debtors' }
        }
        return { success: false, message: 'Failed to pre-validate debtors' }
    }
}

export const submitBatchToTriumph = async (
    invoiceIds: string[],
    debtorOverrides?: Record<string, TriumphDebtorResult>
): Promise<{ success: boolean; data?: { jobId: string }; message?: string }> => {
    try {
        const response = await axios.post<{ success: boolean; data?: { jobId: string }; message?: string }>('/triumph/submit-batch', {
            invoiceIds,
            debtorOverrides,
        })
        return response.data
    } catch (error) {
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to submit batch to Triumph' }
        }
        return { success: false, message: 'Failed to submit batch to Triumph' }
    }
}

// ==================== RTS DEBTOR API ====================

export interface RTSDebtorResult {
    _id: string
    name: string
    mc_number: string
    rating: string
    averageDaysToPayPast60: string | null
    city: string
    state: string
    lastActivity?: string
    lastCreditScoreChange?: string
    phone?: string
    email?: string
    dot_number?: string
    address1?: string
    address2?: string
    collectionQueue?: string
    previousRating?: string
    invoicesIncludedTotal?: number
}

export interface RTSPreValidationResult {
    invoiceId: string
    invoiceNumber?: string
    brokerName: string
    mcNumber: string
    matchStatus: 'exact_mc' | 'no_match' | 'error'
    matchedDebtor?: RTSDebtorResult
    searchResults: RTSDebtorResult[]
    error?: string
}

interface RTSPreValidationResponse {
    requireConfirmation: boolean
    results: RTSPreValidationResult[]
}

export const searchRTSDebtors = async (
    searchTerm: string
): Promise<{ success: boolean; data?: RTSDebtorResult[]; message?: string; needsReauth?: boolean }> => {
    try {
        const response = await axios.post<{ success: boolean; data?: RTSDebtorResult[]; message?: string }>('/rts/debtors/search', { searchTerm })
        return response.data
    } catch (error) {
        if (axios.isAxiosError(error)) {
            const status = error.response?.status
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to search RTS debtors',
                needsReauth: status === 401 || status === 403,
            }
        }
        return { success: false, message: 'Failed to search RTS debtors' }
    }
}

export const preValidateRTSDebtors = async (invoiceIds: string[]): Promise<{ success: boolean; data?: RTSPreValidationResponse; message?: string }> => {
    try {
        const response = await axios.post<{ success: boolean; data?: RTSPreValidationResponse; message?: string }>('/rts/debtors/pre-validate', {
            invoiceIds,
        })
        return response.data
    } catch (error) {
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to pre-validate RTS debtors' }
        }
        return { success: false, message: 'Failed to pre-validate RTS debtors' }
    }
}

export interface RTSOverviewData {
    client: {
        clientNumber: string
        clientName: string
        clientKey: number
        arBalance: number
        reserveAmount: number
        accountRep: { name: string; email: string; phone: string }
    }
    unfundedInvoices: { heldCount: number; pendingCount: number; deniedCount: number; agingDays: number }
    purchasedInvoices: { actionNeededCount: number; nearingRecourseCount: number }
    currentPurchase: {
        purchaseId: string
        submittedDate: string
        status: string
        invoicesCount: number
        heldCount: number
        deniedCount: number
        pendingCount: number
    } | null
    recentPurchases: Array<{ purchaseNumber: string; purchaseDate: string; amountPaid: number; invoicesCount: number }>
    topDebtors: Array<{ name: string; amount: number }>
}

// ==================== WEX DEBTOR API ====================

export type { WexDebtor as WexDebtorResult, WexPreValidationResult }

export const searchWexDebtors = async (searchTerm: string): Promise<{ success: boolean; data?: WexDebtor[]; message?: string; needsReauth?: boolean }> => {
    try {
        const response = await axios.post<{ success: boolean; data?: WexDebtor[]; message?: string }>('/wex/debtors/search', { searchTerm })
        return response.data
    } catch (error) {
        if (axios.isAxiosError(error)) {
            // 401 = WEX cookies expired — surface needsReauth so the search UI can render an
            // inline Reconnect button instead of swallowing the failure as "no results".
            const status = error.response?.status
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to search WEX debtors',
                needsReauth: status === 401 || status === 403,
            }
        }
        return { success: false, message: 'Failed to search WEX debtors' }
    }
}

// ==================== STRATOPAY DEBTOR API ====================

/**
 * A Stratopay (Addio) debtor. Addio nests the readable name under `company.data.name`
 * (the top-level `name` is null) and exposes no MC number in search results.
 */
export interface StratopayDebtorResult {
    id: number
    ref_code?: string
    status_description?: string
    company?: { data?: { name?: string } }
}

/**
 * "Search" OTR brokers. OTR's Carrier TMS API has no broker directory search — only a single-MC
 * lookup (`GET /broker-check/:mc`) — so the search term must be an MC number; the one matching broker
 * (if any) is returned as a single-element array to fit the shared debtor-search shape. A name term
 * (non-numeric) returns empty, and a 404 (broker unknown to OTR) is an empty result, not an error.
 */
export const searchOtrSolutionsDebtors = async (
    searchTerm: string
): Promise<{ success: boolean; data?: OtrSolutionsBrokerCheck[]; message?: string; needsReauth?: boolean }> => {
    const mc = (searchTerm || '').replace(/\D/g, '')
    if (!mc) return { success: true, data: [] }
    try {
        const response = await axios.get<{ success: boolean; data?: OtrSolutionsBrokerCheck | null }>(`/factoring/otr-solutions/broker-check/${mc}`)
        const broker = response.data?.data
        return { success: true, data: broker && broker.McNumber ? [broker] : [] }
    } catch (error) {
        if (axios.isAxiosError(error)) {
            const status = error.response?.status
            if (status === 404) return { success: true, data: [] }
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to check the OTR broker',
                needsReauth: status === 401 || status === 403,
            }
        }
        return { success: false, message: 'Failed to check the OTR broker' }
    }
}

export const searchStratopayDebtors = async (
    searchTerm: string
): Promise<{ success: boolean; data?: StratopayDebtorResult[]; message?: string; needsReauth?: boolean }> => {
    try {
        const response = await axios.post<{ success: boolean; data?: StratopayDebtorResult[]; message?: string }>('/factoring/stratopay/debtors/search', {
            searchTerm,
        })
        return response.data
    } catch (error) {
        if (axios.isAxiosError(error)) {
            const status = error.response?.status
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to search Stratopay debtors',
                needsReauth: status === 401 || status === 403,
            }
        }
        return { success: false, message: 'Failed to search Stratopay debtors' }
    }
}

interface WexFundingAccountOption {
    accountId: string
    method: WexFundingMethod
    label: string
}

/**
 * Fetch the carrier's WEX funding accounts from the live AssignmentSchedule page.
 * Requires at least one billing id — the page server-renders the dropdown per
 * call. Use any in-flight or recent billing id; the list is the same for all
 * billings on the same WEX account.
 */
const getWexFundingAccounts = async (
    billingIds: string[]
): Promise<{ success: boolean; data?: WexFundingAccountOption[]; message?: string; needsReauth?: boolean }> => {
    try {
        const response = await axios.get<{ success: boolean; data?: WexFundingAccountOption[]; message?: string }>('/wex/funding-accounts', {
            params: { billingIds: billingIds.join(',') },
        })
        return response.data
    } catch (error) {
        if (axios.isAxiosError(error)) {
            const status = error.response?.status
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch WEX funding accounts',
                needsReauth: status === 401 || status === 403,
            }
        }
        return { success: false, message: 'Failed to fetch WEX funding accounts' }
    }
}

interface WexAssignmentSubmitResult {
    scheduleNumber: string
    invoiceAmount: number
    method: string
    accountId: string
}

/**
 * Submit a WEX assignment schedule (the actual factor handoff). Mirrors what
 * common.js's save_assignment_schedule(...,'submit') does on the FleetDocs UI.
 */
const submitWexAssignment = async (
    billingIds: string[],
    accountId: string,
    method: WexFundingMethod
): Promise<{ success: boolean; data?: WexAssignmentSubmitResult; message?: string; needsReauth?: boolean }> => {
    try {
        const response = await axios.post<{ success: boolean; data?: WexAssignmentSubmitResult; message?: string }>('/wex/assignments/submit', {
            billingIds,
            accountId,
            method,
        })
        return response.data
    } catch (error) {
        if (axios.isAxiosError(error)) {
            const status = error.response?.status
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to submit WEX assignment',
                needsReauth: status === 401 || status === 403,
            }
        }
        return { success: false, message: 'Failed to submit WEX assignment' }
    }
}

const getRTSOverview = async (): Promise<{ success: boolean; data?: RTSOverviewData; message?: string }> => {
    try {
        const response = await axios.get<{ success: boolean; data?: RTSOverviewData; message?: string }>('/rts/overview')
        return response.data
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 401) {
            return { success: false, message: 'AUTH_REQUIRED' }
        }
        return { success: false, message: 'Failed to fetch RTS overview' }
    }
}

const invoiceApi = {
    // Driver invoice
    generateDriverInvoice,
    sendDriverInvoiceEmail,
    listDriverInvoices,
    getDriverInvoice,
    deleteDriverInvoice,
    // Broker invoice
    generateBrokerInvoice,
    listBrokerInvoices,
    listInvoiceRows,
    getInvoiceStats,
    getBrokerInvoice,
    updateBrokerInvoiceStatus,
    bulkUpdateBrokerInvoiceStatus,
    bulkCloseLoads,
    generateInvoicePdf,
    deleteBrokerInvoice,
    removeInvoicesFromBatch,
    getNextInvoiceNumber,
    updateLoadInvoiceData,
    updateLoadLineHaulRate,
    sendEmailInvoice,
    getBatchMergedPdf,
    createBatch,
    groupInvoicesIntoBatch,
    syncRTSInvoices,
    syncWinfactorInvoices,
    syncStratopayInvoices,
    syncArtisproInvoices,
    syncOtrSolutionsInvoices,
    syncTriumphInvoices,
    getWexFundingAccounts,
    submitWexAssignment,
    fetchRtsInvoicePdf,
    fetchTriumphInvoicePdf,
    fetchWexInvoicePdf,
    resolveInvoicePdf,
    getRTSJobStatus,
    retryBatchFactoring,
    retryBatchRTS,
    retryFactoring,
    cancelBatchRTS,
    getInvoiceEmailActivity,
    getAccountingReadStatus,
    toggleAccountingReadStatus,
    batchToggleAccountingReadStatus,
    getOverviewStats,
    searchTriumphDebtors,
    preValidateTriumphDebtors,
    submitBatchToTriumph,
    getRTSOverview,
}

export const updateAccountingSettings = async (
    projectName: string,
    settings: AccountingSettings
): Promise<{ success: boolean; application?: ClientApplication; message?: string }> => {
    try {
        const response = await axios.put<{ success: boolean; application: ClientApplication }>(`/accountant/settings/${projectName}`, settings)
        return response.data
    } catch (error) {
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to update accounting settings' }
        }
        return { success: false, message: 'Failed to update accounting settings' }
    }
}

// ── Invoice migration (DITAT / QuickManage / Data Truck invoice export → broker invoices) ──

// Re-export the shared contract verbatim (it already carries `provider`) — no local re-shaping,
// so the client can't drift from the server's response.
export type InvoiceMigrationResult = InvoiceMigrationCounts

interface ImportInvoicesResponse {
    success: boolean
    data?: InvoiceMigrationResult
    message?: string
    cancelled?: boolean
    /** True when the server recognized the file but it was unusable (HTTP 422). */
    warning?: boolean
}

/**
 * Upload a TMS invoice export to migrate it into broker invoices. The provider is detected
 * server-side from the header row, so the caller just sends the file. Cancellable via `signal`.
 */
export const importInvoicesMigration = async (file: File, signal?: AbortSignal): Promise<ImportInvoicesResponse> => {
    try {
        const formData = new FormData()
        formData.append('file', file)
        const response = await axios.post<ImportInvoicesResponse>('/tms-providers/import-invoices', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            signal,
        })
        return response.data
    } catch (error) {
        if (axios.isCancel(error)) return { success: false, cancelled: true }
        if (axios.isAxiosError(error)) {
            return { success: false, warning: error.response?.status === 422, message: error.response?.data?.message || 'Failed to import invoices' }
        }
        return { success: false, message: 'Failed to import invoices' }
    }
}

interface BackfillOrphanLinksResult {
    scanned: number
    linked: number
    capped: boolean // true → more orphans remain, re-run after their loads land
}

interface BackfillOrphanLinksResponse {
    success: boolean
    data?: BackfillOrphanLinksResult
    message?: string
}

/**
 * Link orphan migrated invoices (imported before their load existed) to loads migrated since.
 * Idempotent and project-scoped — safe to re-run; `capped` true means run again to drain the rest.
 */
export const backfillOrphanInvoiceLinks = async (): Promise<BackfillOrphanLinksResponse> => {
    try {
        const response = await axios.post<BackfillOrphanLinksResponse>('/tms-providers/backfill-orphan-invoice-links')
        return response.data
    } catch (error) {
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to link orphan invoices' }
        }
        return { success: false, message: 'Failed to link orphan invoices' }
    }
}

export default invoiceApi
